import React, { useState, useEffect, useContext, useRef } from 'react';
import { FaPause, FaPlay, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { DContext } from '../../context/DataContext';
import { useTranslation } from 'react-i18next';

const AdVertical = () => {
  const [adMedia, setAdMedia] = useState({ url: '', sourceType: 'video' });
  const { BeURL } = useContext(DContext);
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!BeURL) {
      return;
    }

    fetch(`${BeURL}/fetch-ad-video`)
      .then((res) => res.json())
      .then((data) => setAdMedia({ url: data.url || '', sourceType: data.sourceType || 'video' }))
      .catch((err) => console.error('Error fetching ad video:', err));
  }, [BeURL]);

  const getYoutubeEmbedUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const regexes = [
      /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /[?&]v=([a-zA-Z0-9_-]{11})/
    ];

    for (const regex of regexes) {
      const match = raw.match(regex);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}&rel=0&modestbranding=1`;
      }
    }

    return null;
  };

  const handleTogglePlayback = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      return;
    }

    video.muted = false;
    video.volume = 1;
    video.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {
      setIsPlaying(false);
    });
  };

  if (!adMedia.url) {
    return null
  }

  const isYouTube = adMedia.sourceType === 'youtube' || Boolean(getYoutubeEmbedUrl(adMedia.url));
  const youtubeEmbedUrl = getYoutubeEmbedUrl(adMedia.url);
  const src = String(adMedia.url || '').startsWith('http://') || String(adMedia.url || '').startsWith('https://')
    ? adMedia.url
    : `${BeURL}/${adMedia.url}`;

  return (
    <div className='mx-auto w-full max-w-xl'>
      <div className='relative overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-[0_22px_50px_rgba(15,23,42,0.16)]'>
        <div className='relative overflow-hidden'>
          {isYouTube && youtubeEmbedUrl ? (
            <iframe
              src={youtubeEmbedUrl}
              title='Ad YouTube video'
              className='h-[340px] w-full sm:h-[430px]'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              referrerPolicy='strict-origin-when-cross-origin'
              allowFullScreen
            />
          ) : (
            <video
              ref={videoRef}
              src={src}
              loop
              playsInline
              preload='metadata'
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              className='h-[340px] w-full object-cover sm:h-[430px]'
            />
          )}

          {!isYouTube && (
            <button
              type='button'
              onClick={handleTogglePlayback}
              aria-label={isPlaying ? t('homepage.video.paused') : t('homepage.video.tapToPlay')}
              className='group absolute inset-0 z-10 flex items-center justify-center'
            >
              {!isPlaying && (
                <span className='absolute h-20 w-20 rounded-full bg-white/50 animate-ping sm:h-24 sm:w-24' />
              )}
              <span className='relative inline-flex h-16 w-16 items-center justify-center rounded-full border border-white/90 bg-white/90 text-[#1739bd] shadow-[0_12px_34px_rgba(15,23,42,0.25)] transition group-hover:scale-105 sm:h-20 sm:w-20'>
                {isPlaying ? <FaPause className='text-xl sm:text-2xl' /> : <FaPlay className='ml-0.5 text-xl sm:text-2xl' />}
              </span>
            </button>
          )}

          <div className='absolute left-3 top-3 inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/85 px-3 py-1 text-xs font-semibold text-slate-800 backdrop-blur'>
            {isYouTube ? <FaPlay className='text-[#1739bd]' /> : isPlaying ? <FaVolumeUp className='text-[#1739bd]' /> : <FaVolumeMute className='text-slate-500' />}
            {isYouTube ? 'YouTube' : isPlaying ? t('homepage.video.playing') : t('homepage.video.paused')}
          </div>

          <div className='pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 via-white/80 to-transparent p-3 sm:p-4'>
            <p className='text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500'>{t('homepage.video.eyebrow')}</p>
            <p className='mt-1 text-sm font-semibold text-slate-900 sm:text-base'>{t('homepage.video.title')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdVertical;
